import { Button } from "@mui/material";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useIsExpertOrGreater } from "../data/Results/useIsExpertOrGreater";
import { Card } from "../shared/Card";
import { CopyToClipboard } from "../shared/CopyToClipboard";
import { FormRow } from "../shared/FormLayout/FormRow";
import { getTranslatedSubLocationName } from "../shared/helpers/SubLocationHelpers";
import { Measurement, MeasurementDetail, Result } from "../shared/interfaces/Result";
import { getSubAssetType } from "../shared/interfaces/SurveyableAsset";
import { MeasurementStatusEditorDialog } from "./MeasurementStatusEditorDialog";
import RAGStatusChip from "./RAGStatusChip";

interface MeasurementOverviewCardProps {
  measurementDetail: MeasurementDetail<Measurement>;
  captureId: number;
  selectedResult?: Result;
  selectedAssetType: string;
  onChange: () => void;
}

export function MeasurementOverviewCard(props: MeasurementOverviewCardProps) {
  const { customerId } = useParams<{ customerId: string }>();

  const { selectedResult, measurementDetail, captureId, onChange } = props;

  const substationName =
    selectedResult?.dataValue.SubstationSummary?.SubstationName ??
    selectedResult?.dataValue.MetaData?.SubstationGroup?.SubstationName;
  const isExpertOrGreater = useIsExpertOrGreater(
    Number(customerId),
    selectedResult?.dataValue.SubstationSummary?.SubstationId,
    substationName,
  );

  const { t } = useTranslation();

  const [statusEditorOpen, setStatusEditorOpen] = React.useState(false);

  const data = useMemo(
    () => [
      {
        label: t("results.panelNumber", { val: getSubAssetType(props.selectedAssetType) }),
        value: measurementDetail.panelNo,
      },
      {
        label: t("results.panelName", { val: getSubAssetType(props.selectedAssetType) }),
        value: measurementDetail.panelName,
      },
      {
        label: t("results.component"),
        value: t(`MeasurementComponents.${measurementDetail.measurement.ComponentType}`),
      },
      {
        label: t("results.subLocation"),
        value: measurementDetail.measurement.SubLocation
          ? getTranslatedSubLocationName(
              measurementDetail.measurement.SubLocation,
              measurementDetail.measurement.ComponentType,
              props.selectedResult?.dataValue?.MetaData?.SubstationGroup?.CableNamingScheme,
            )
          : "",
      },
      {
        label: t("results.insulation"),
        value:
          measurementDetail.measurement.Insulation &&
          t(`UTP2.insulationType.${measurementDetail.measurement.Insulation}`),
      },
      {
        label: t("results.switchPosition"),
        value:
          measurementDetail.measurement.SwitchPosition &&
          t(`UTP2.switchPosition.${measurementDetail.measurement.SwitchPosition}`),
      },
      {
        label: t("common.status"),
        value: isExpertOrGreater.data?.valueOf() ? (
          <>
            <RAGStatusChip ragStatus={measurementDetail.measurement.RAGStatus} />
            <Button onClick={handleUpdateStatusClick}>{t("common.update")}</Button>
          </>
        ) : (
          <>
            <RAGStatusChip ragStatus={measurementDetail.measurement.RAGStatus} />
          </>
        ),
        textValue: measurementDetail.measurement.RAGStatus,
      },
    ],
    [t, measurementDetail, isExpertOrGreater.data, props.selectedResult, props.selectedAssetType],
  );

  function handleUpdateStatusClick() {
    setStatusEditorOpen(true);
  }

  function handleUpdateStatusCancelled() {
    setStatusEditorOpen(false);
  }

  function handleStatusUpdate() {
    setStatusEditorOpen(false);
    onChange();
  }

  function constructClipboardText() {
    return data.map((d) => `${d.label}, ${d.textValue != null ? d.textValue : d.value}`).join("\n");
  }

  return (
    <>
      <MeasurementStatusEditorDialog
        open={statusEditorOpen}
        measurementDetail={measurementDetail}
        captureId={captureId}
        customerId={Number(customerId)}
        onCancel={handleUpdateStatusCancelled}
        onEdited={handleStatusUpdate}
      />

      <Card title={t("results.measurementOverview")} action={<CopyToClipboard textToCopy={constructClipboardText()} />}>
        {data.map((d) => (
          <FormRow label={d.label} value={d.value} />
        ))}
      </Card>
    </>
  );
}
